import React, { useState, FC } from 'react';
import { useTranslate } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import TransactionView from '~/components/TransactionsHistory/TransactionView';
import { DateTimeField, ColoredAmountField, RowMenu, ColoredField } from './cellComponents';
import { Paper } from '@material-ui/core';
import { ChipField, VirtualizedList } from '~/components';
import {
  selectors,
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';
import {
  Column,
  Individual,
  Business,
  Transaction,
  TransactionsFilters,
  Partner,
  Align,
  Account,
  PnLAccount,
} from '~/types';
import { getTransactions } from '~/api/transactionsHistory';
import Filters from './Filters';
import { NoPermissions } from '../ra';
import { Props as FieldProps } from './cellComponents';
import OperationTypeField from '~/components/OperationTypeField';
import { Longdash } from '~/utils';
import { getAccountResourceByProfileType } from '~/utils/profileType';
import { Target } from '~/types/profile';
import OperationSubtypeField from '~/resources/headTransactions/components/list/OperationSubtypeField';

const label = (key: string): string => `components.TransactionsHistory.${key}`;

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 230px)',
  },
  header: {
    flex: '0 0 auto',
  },
}));

interface TransactionHistoryProps {
  record: Individual | Business | Partner | Account | PnLAccount;
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts' | 'accounts';
  permissions: string[];
}

const ellipsis = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const getListPermission = (
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts' | 'accounts',
  accountResource: Target | undefined,
  permissions: string[]
) => {
  switch (resource) {
    case 'individuals':
      return permissions.includes('individual.transaction.list');
    case 'businesses':
      return permissions.includes('business.transaction.list');
    case 'partners':
      return permissions.includes('partner.transaction.list');
    case 'tenant/accounts':
      return permissions.includes('tenant.account.transaction.list');
    case 'accounts':
      if (accountResource === 'individuals')
        return permissions.includes('individual.transaction.list');
      if (accountResource === 'businesses')
        return permissions.includes('business.transaction.list');
      if (accountResource === 'partners') return permissions.includes('partner.transaction.list');
      if (accountResource === 'tenant')
        return permissions.includes('tenant.account.transaction.list');
      return false;
    default:
      return false;
  }
};

const getViewPermission = (
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts' | 'accounts',
  accountResource: Target | undefined,
  permissions: string[]
) => {
  switch (resource) {
    case 'individuals':
      return permissions.includes('individual.transaction.view');
    case 'businesses':
      return permissions.includes('business.transaction.view');
    case 'partners':
      return permissions.includes('partner.transaction.view');
    case 'tenant/accounts':
      return permissions.includes('tenant.account.transaction.view');
    case 'accounts':
      if (accountResource === 'individuals')
        return permissions.includes('individual.transaction.view');
      if (accountResource === 'businesses')
        return permissions.includes('business.transaction.view');
      if (accountResource === 'partners') return permissions.includes('partner.transaction.view');
      if (accountResource === 'tenant')
        return permissions.includes('tenant.account.transaction.view');
      return false;
    default:
      return false;
  }
};

const getApiTargetAndResourceId = (
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts' | 'accounts',
  accountResource: Target | undefined,
  record: Individual | Business | Partner | Account | PnLAccount
): { apiTarget: Target; apiResourceId: string | undefined } => {
  if (resource === 'accounts' && accountResource === 'tenant') {
    return { apiTarget: 'tenant', apiResourceId: undefined };
  }
  if (resource === 'accounts' && accountResource && 'profileId' in record) {
    return { apiTarget: accountResource, apiResourceId: record?.profileId };
  }
  if (resource === 'tenant/accounts') {
    return { apiTarget: 'tenant', apiResourceId: undefined };
  }
  return { apiTarget: resource, apiResourceId: record.id };
};

const TransactionHistory: FC<TransactionHistoryProps> = (props) => {
  const { record, permissions, resource } = props;
  const theme = useTheme();
  const classes = useStyles();
  const translate = useTranslate();
  const translateLabel = (key: string): string => translate(label(key));
  const dispatch = useDispatch();
  // Distinct from `resource` (which is also used for API calls and
  // permission checks) so this tab's filter/list state doesn't collide
  // with the redux state of the standalone resource list of the same name.
  const filterResource = `${resource}-transactions`;
  const filters = useSelector(selectors.getFilters<TransactionsFilters>(filterResource));
  const list = useSelector(selectors.getList(filterResource));

  const isAccountResource = resource === 'accounts';
  const accountResource =
    isAccountResource && 'profileType' in record
      ? getAccountResourceByProfileType(record.profileType)
      : undefined;
  const target: Target =
    isAccountResource && 'profileType' in record
      ? (getAccountResourceByProfileType(record.profileType) as Target)
      : (resource as Target);

  const loadNextPage = async () => {
    if (isNextPageLoading || (!list.cursors.next && list.data.length > 0) || !record) return;

    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource: filterResource, queryToken }));
      const { apiTarget, apiResourceId } = getApiTargetAndResourceId(
        resource,
        accountResource,
        record
      );
      const { data } = await getTransactions({
        target: apiTarget,
        resourceId: apiResourceId,
        next: list.cursors.next,
        filters,
      });
      const responseData = {
        ...data,
        cursors: data.cursors,
        records: data.transactions as any[],
      };
      dispatch(getVirtualizedListSuccess({ resource: filterResource, responseData, queryToken }));
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource: filterResource, error: err, queryToken }));
    }
  };

  const RowField = (Field: any) => {
    return (props: FieldProps) => {
      const style =
        props.rowData.status === 'REJECTED' ? { color: theme.palette.error.main } : undefined;
      return (
        <Field {...props} style={style}>
          {props.children}
        </Field>
      );
    };
  };

  const maskAccountNumber = (accountNumber?: string) => {
    if (!accountNumber) return null;
    const first = accountNumber.slice(0, 3);
    const last = accountNumber.slice(-4);
    const masked = '*'.repeat(3);
    return `${first}${masked}${last}`;
  };

  const getRunningBalance = (rowData: Transaction) => {
    const currentPosted = rowData?.details?.runningBalances?.find(
      (item) => item?.code === 'CURRENT_POSTED'
    )?.amount;
    return currentPosted;
  };

  // const isBusiness =
  //   resource === 'businesses' || (resource === 'accounts' && accountResource === 'businesses');
  // const isIndividual =
  //   resource === 'individuals' || (resource === 'accounts' && accountResource === 'individuals');
  // const isPartner =
  //   resource === 'partners' || (resource === 'accounts' && accountResource === 'partners');
  const isTenant =
    resource === 'tenant/accounts' || (resource === 'accounts' && accountResource === 'tenant');

  const columns: Column<Transaction>[] = [
    // Date & Time
    {
      selectCellData: (rowData) => rowData.createdAt,
      cellDataWrapperComponent: RowField(DateTimeField),
      header: translateLabel('datetime'),
      style: {
        flexBasis: 160,
        minWidth: 160,
        paddingRight: 0,
      },
    },
    // Posted amount
    {
      selectCellData: (rowData: Transaction) =>
        rowData.status !== 'PENDING' && rowData.postedAmount,
      cellDataWrapperComponent: RowField((props: FieldProps) => {
        const { rowData } = props;
        if (
          !rowData.headTransactionId &&
          !rowData.postedAmount &&
          (rowData.status === 'COMPLETED' || rowData.status === 'REJECTED')
        ) {
          return <ChipField label={translateLabel('external')} />;
        } else {
          return (
            <ColoredField {...props}>
              <ColoredAmountField {...props} />
            </ColoredField>
          );
        }
      }),
      id: 'postedAmount',
      header: translateLabel('postedAmount'),
      headerAlign: 'right' as Align,
      cellAlign: 'right' as Align,
      style: {
        flexBasis: 120,
        minWidth: 120,
        paddingRight: 0,
      },
    },
    // Base amount
    {
      selectCellData: (rowData: Transaction) => rowData.baseAmount,
      cellDataWrapperComponent: RowField(ColoredAmountField),
      id: 'baseAmount',
      header: translateLabel('baseAmount'),
      headerAlign: 'right' as Align,
      cellAlign: 'right' as Align,
      style: {
        flexBasis: 120,
        minWidth: 120,
        paddingRight: 0,
      },
    },
    // Operation type
    {
      selectCellData: (rowData) => rowData.operationType,
      cellDataWrapperComponent: RowField((props: FieldProps) => (
        <ColoredField {...props}>
          {props?.rowData?.operationSubtype ? (
            <OperationSubtypeField
              keyPrefix='operationSubtype'
              operationSubtype={props?.rowData?.operationSubtype}
              serviceId={props?.rowData?.details?.service?.id}
              label={translate(`operationType.${props?.rowData?.operationType}`)}
            />
          ) : (
            <OperationTypeField operationType={props.children} />
          )}
        </ColoredField>
      )),
      id: 'operationType',
      header: translateLabel('operationType'),
      style: {
        flexBasis: 110,
        // maxWidth: 200,
        minWidth: 110,
        paddingRight: 0,
      },
    },
    // Details
    {
      selectCellData: (rowData: Transaction) => rowData.description,
      cellDataWrapperComponent: RowField((props: FieldProps) => {
        return <ColoredField {...props} style={ellipsis} />;
      }),
      header: translateLabel('details'),
      style: {
        flexBasis: 350,
        minWidth: 350,
        // maxWidth: 350,
        display: 'inline',
        marginTop: 'auto',
        marginBottom: 'auto',
        paddingRight: 0,
      },
    },
    // Account or PI (businesses, individuals, partners)
    ...(!isTenant && !isAccountResource
      ? [
          {
            selectCellData: (rowData: Transaction) => rowData,
            cellDataWrapperComponent: RowField((props: FieldProps) => {
              const transaction = props.rowData;
              const isSender = (transaction?.amount?.value || 0) < 0;
              const party = isSender ? transaction?.sender : transaction?.recipient;
              const accountNumber = party?.accountNumber;
              const isExternal =
                !transaction.headTransactionId &&
                !transaction.postedAmount &&
                (transaction.status === 'COMPLETED' || transaction.status === 'REJECTED');
              if (!isExternal && !accountNumber) return Longdash;
              if (isExternal) {
                return (
                  <ColoredField {...props} style={{ ...ellipsis, fontWeight: '500' }}>
                    {translateLabel('externalSource')}
                  </ColoredField>
                );
              }
              return <ColoredField {...props}>{maskAccountNumber(accountNumber)}</ColoredField>;
            }),
            id: 'accountNumber',
            header: translateLabel('accountOrPI'),
            style: {
              minWidth: 110,
              flexBasis: 110,
              paddingRight: 0,
            },
          },
        ]
      : []),
    // Running balance
    {
      selectCellData: (rowData) => getRunningBalance(rowData),
      cellDataWrapperComponent: RowField(ColoredAmountField),
      id: 'runningBalance',
      header: translateLabel('runningBalance'),
      cellAlign: 'right',
      headerAlign: 'right',
      style: {
        flexBasis: 120,
        minWidth: 120,
        paddingRight: 0,
      },
    },
    {
      selectCellData: (rowData) => rowData,
      cellDataWrapperComponent: RowMenu,
      header: '',
      width: 50,
      style: {
        flexBasis: 50,
        paddingRight: 0,
      },
    },
  ];

  const [showData, setShowData] = useState<Transaction | null>(null);
  const handleShowOpen = (data: Transaction) => setShowData(data);
  const handleShowClose = () => setShowData(null);

  const { hasNextPage, isNextPageLoading, data } = list;

  if (!record) return null;
  if (!permissions) return null;
  if (!getListPermission(resource, accountResource, permissions)) return <NoPermissions />;
  const viewPermission = getViewPermission(resource, accountResource, permissions);
  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <Filters
            record={record}
            resource={resource}
            filterResource={filterResource}
            target={target}
          />
        </div>
        <Paper style={{ flex: '1 1 auto', overflow: 'hidden' }}>
          <VirtualizedList<Transaction>
            resource={filterResource}
            columns={columns}
            hasNextPage={hasNextPage}
            filters={filters}
            isNextPageLoading={isNextPageLoading}
            data={data}
            loadNextPage={loadNextPage}
            onRowClick={viewPermission ? handleShowOpen : undefined}
            // root's height is already computed from the viewport (see classes.root),
            // so let the table fill exactly what Paper gives it instead of forcing a
            // taller minimum that would push content past Paper's own scroll boundary.
            minHeight={0}
          />
        </Paper>
      </div>
      <TransactionView
        record={record}
        target={target}
        transaction={showData}
        onClose={handleShowClose}
      />
    </>
  );
};

export default TransactionHistory;
